import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api, ApiRequestError } from '../../../lib/api'
import { useAuthStore } from '../../../stores/authStore'
import { useOnboardingV2Store } from '../../../stores/onboardingV2Store'
import { providerLabel, useProviderCatalog } from '../../../lib/providers'
import { ProviderSelect } from '../../ProviderSelect'
import OnboardingLayout from './OnboardingLayout'
import PrimaryButton from './PrimaryButton'
import { INPUT_CLASS, LABEL_CLASS } from './formStyles'
import { stepsForMode } from './steps'

interface ApiKeyScreenProps {
  onContinue: () => void
}

type VerifyState = 'idle' | 'verifying' | 'valid' | 'invalid'

// BYO key gate. The user picks a PROVIDER only — model choice is system-owned
// (routing table in backend/config/llm_models.py; owner decision 2026-07-20).
// Shares ProviderSelect and the /api/v1/user/apikey verify+save endpoints
// with Settings (parity invariant).
export default function ApiKeyScreen({ onContinue }: ApiKeyScreenProps) {
  const navigate = useNavigate()
  const fetchUser = useAuthStore((s) => s.fetchUser)
  const fetchState = useOnboardingV2Store((s) => s.fetchState)
  const skip = useOnboardingV2Store((s) => s.skip)

  const { providers } = useProviderCatalog()
  const [provider, setProvider] = useState('anthropic')
  const [rawKey, setRawKey] = useState('')
  const [showKey, setShowKey] = useState(false)
  const [verifyState, setVerifyState] = useState<VerifyState>('idle')
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [skipping, setSkipping] = useState(false)

  const handleVerify = async () => {
    if (!rawKey.trim()) return
    setVerifyState('verifying')
    setError(null)
    try {
      const res = await api.post<{ valid: boolean; message: string }>(
        '/api/v1/user/apikey/verify',
        { raw_key: rawKey, provider },
      )
      setVerifyState(res.valid ? 'valid' : 'invalid')
      if (!res.valid) setError(res.message)
    } catch {
      setVerifyState('invalid')
      setError('We could not verify that key. Check it and try again.')
    }
  }

  const handleSaveAndContinue = async () => {
    if (verifyState !== 'valid') return
    setSaving(true)
    setError(null)
    try {
      await api.put('/api/v1/user/apikey', { raw_key: rawKey, provider })
      await fetchUser()
      await fetchState()
      onContinue()
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.error.message : 'Could not save your key.')
      setSaving(false)
    }
  }

  const handleSkip = async () => {
    setSkipping(true)
    setError(null)
    try {
      await skip()
      await fetchUser()
      navigate('/library', { replace: true })
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.error.message : "Couldn't skip — try again.")
    } finally {
      setSkipping(false)
    }
  }

  return (
    <OnboardingLayout steps={stepsForMode('byo')} activeKey="api_key">
      <div className="mx-auto w-full max-w-[480px]">
        <header className="mb-6 text-center">
          <h1 className="font-serif text-[28px] font-semibold text-ob-ink md:text-3xl">
            Connect your AI key
          </h1>
          <p className="mt-2 font-body text-base leading-relaxed text-ob-secondary">
            Proof of Work uses Anthropic (Claude) to read and organise your professional history.
            Your key is sent straight to Anthropic — we never store it in plaintext.
          </p>
        </header>

        <label htmlFor="ob-provider" className={LABEL_CLASS}>
          AI provider
        </label>
        <div className="mb-4">
          <ProviderSelect
            id="ob-provider"
            value={provider}
            onChange={(p) => {
              setProvider(p)
              setVerifyState('idle')
              setError(null)
            }}
            providers={providers}
            className={INPUT_CLASS}
          />
        </div>

        <label htmlFor="ob-apikey" className={LABEL_CLASS}>
          {providerLabel(providers, provider)} API key
        </label>
        <div className="relative">
          <input
            id="ob-apikey"
            type={showKey ? 'text' : 'password'}
            value={rawKey}
            onChange={(e) => {
              setRawKey(e.target.value)
              setVerifyState('idle')
              setError(null)
            }}
            placeholder="sk-ant-…"
            autoComplete="off"
            spellCheck={false}
            className={`${INPUT_CLASS} pe-16`}
          />
          <button
            type="button"
            onClick={() => setShowKey((v) => !v)}
            className="absolute inset-y-0 end-0 flex min-h-[44px] items-center px-3 font-body text-xs font-semibold uppercase tracking-wider text-ob-secondary hover:text-ob-ink"
          >
            {showKey ? 'Hide' : 'Show'}
          </button>
        </div>

        {verifyState === 'valid' && (
          <p className="mt-2 flex items-center gap-1.5 font-body text-sm text-ob-ink">
            <span className="material-symbols-outlined text-[18px] text-ob-accent" aria-hidden="true">
              check_circle
            </span>
            Key verified.
          </p>
        )}
        {error && (
          <p className="mt-2 font-body text-sm text-ob-error" role="alert">
            {error}
          </p>
        )}

        <details className="mt-4">
          <summary className="cursor-pointer font-body text-sm text-ob-secondary hover:text-ob-ink">
            Where do I find my API key?
          </summary>
          <ol className="mt-2 list-decimal space-y-1 ps-5 font-body text-sm text-ob-secondary">
            <li>Sign in to the Anthropic Console.</li>
            <li>
              Open{' '}
              <a
                href="https://console.anthropic.com/settings/keys"
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-ob-ink"
              >
                Settings → API keys
              </a>
              .
            </li>
            <li>Create a key and paste it here. It starts with “sk-ant-”.</li>
          </ol>
        </details>

        <div className="mt-8">
          {verifyState === 'valid' ? (
            <PrimaryButton className="w-full" disabled={saving} onClick={() => void handleSaveAndContinue()}>
              {saving ? 'Saving…' : 'Continue →'}
            </PrimaryButton>
          ) : (
            <PrimaryButton
              className="w-full"
              disabled={!rawKey.trim() || verifyState === 'verifying'}
              onClick={() => void handleVerify()}
            >
              {verifyState === 'verifying' ? 'Validating…' : 'Validate key'}
            </PrimaryButton>
          )}
        </div>

        <div className="mt-4 text-center">
          <button
            type="button"
            onClick={() => void handleSkip()}
            disabled={skipping}
            className="font-body text-sm text-ob-secondary transition-colors hover:text-ob-ink disabled:opacity-50"
          >
            {skipping ? 'Skipping…' : 'Skip for now — go to Library'}
          </button>
        </div>
      </div>
    </OnboardingLayout>
  )
}
